import { DS_INPUT, Field } from "@/components/ds/field";
import { btn } from "@/components/ds/styles";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { PublicShell } from "@/components/public/public-shell";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import logoWide from "@/assets/drsara-logo-maroon.png.asset.json";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "إعادة تعيين كلمة المرور — د. سارة النحّاس" },
      { name: "description", content: "اختيار كلمة مرور جديدة للحساب." },
      { name: "robots", content: "noindex, nofollow, noarchive, nosnippet" },
    ],
  }),
  component: ResetPasswordPageInShell,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Supabase places the recovery session on the URL hash (#access_token=...&type=recovery).
    // The client SDK auto-parses this and fires `onAuthStateChange('PASSWORD_RECOVERY')`.
    let mounted = true;
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (!mounted) return;
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    // Also check for an already-established session (in case the event fired before mount).
    supabase.auth.getSession().then(({ data: s }) => {
      if (mounted && s.session) setReady(true);
    });
    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (password.length < 8) {
      toast.error("كلمة المرور قصيرة", { description: "8 أحرف على الأقل." });
      return;
    }
    if (password !== confirm) {
      toast.error("الكلمتان غير متطابقتين");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("تم تحديث كلمة المرور");
      navigate({ to: "/portal", replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(/pwned|breach|weak/i.test(msg) ? "هذه الكلمة مسرّبة، والأفضل كلمة أقوى." : "تعذّر تحديث كلمة المرور.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-cream-200">
      <div className="mx-auto flex min-h-[70svh] max-w-md items-center px-6 py-10">
        <div className="w-full rounded-ds-xl bg-surface p-8 shadow-sm">
          <img src={logoWide.url} alt="د. سارة النحّاس" className="mb-6 h-12 w-auto" />
          <h1 className="font-display text-2xl font-extrabold text-foreground">إعادة تعيين كلمة المرور</h1>
          <p className="mt-2 text-sm text-brown-500">
            {ready
              ? "كلمة مرور جديدة لتسجيل الدخول."
              : "جارٍ التحقّق من رابط الاستعادة…"}
          </p>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <Field id="new-password-field" label="كلمة المرور الجديدة">
              <input
                id="new-password-field"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
                className={DS_INPUT}
              />
            </Field>
            <Field id="confirm-password-field" label="تأكيد كلمة المرور">
              <input
                id="confirm-password-field"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
                className={DS_INPUT}
              />
            </Field>
            <button
              type="submit"
              disabled={submitting || !ready}
              className={`${btn.primary} mt-2 w-full disabled:opacity-50`}
            >
              {submitting ? "لحظة..." : "حفظ كلمة المرور"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordPageInShell() {
  return (
    <PublicShell ground="bg-cream-200">
      <ResetPasswordPage />
    </PublicShell>
  );
}
